import React, { useState } from 'react';
import { MapPin, Phone, Mail, Instagram, Facebook, Twitter } from 'lucide-react';
import InternshipForm from './InternshipForm';
import NewsletterForm from './NewsletterForm';
import { trackSocialClick, trackContactClick } from '../utils/analytics';

const Footer = () => {
  const [internshipFormOpen, setInternshipFormOpen] = useState(false);
  const [newsletterFormOpen, setNewsletterFormOpen] = useState(false);

  return (
    <>
      <InternshipForm isOpen={internshipFormOpen} onClose={() => setInternshipFormOpen(false)} />
      <NewsletterForm isOpen={newsletterFormOpen} onClose={() => setNewsletterFormOpen(false)} />
    <footer className="bg-black border-t border-purple-900/30">
      <div className="container mx-auto px-4 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_63dac94a-0f2a-4ccd-9e98-d528f614ae89/artifacts/78xremtt_IMG_3108.jpeg"
                alt="High Quality Studios"
                className="h-16 w-16 rounded-full object-cover border-2 border-purple-500/30"
              />
            </div>
            <p className="text-gray-400 text-sm">
              Brooklyn's premier recording studio for all your creative needs.
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-2">
                <MapPin size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-400">Brooklyn, NY</span>
              </div>
              <div className="flex items-start space-x-2">
                <Phone size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
                <a 
                  href="tel:+19294131993" 
                  onClick={() => trackContactClick('Phone')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  (929) 413-1993
                </a>
              </div>
              <div className="flex items-start space-x-2">
                <Mail size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
                <a 
                  href="mailto:info@hqstu.com" 
                  onClick={() => trackContactClick('Email')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  info@hqstu.com
                </a>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-purple-900/30">
              <p className="text-xs text-gray-500 mb-1">Business Inquiries:</p>
              <a 
                href="mailto:Trapspade@hqstu.com" 
                onClick={() => trackContactClick('Email - Business')}
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Trapspade@hqstu.com
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#services" className="text-gray-400 hover:text-white transition-colors">
                  Services
                </a>
              </li>
              <li>
                <a href="#booking" className="text-gray-400 hover:text-white transition-colors">
                  Book Now
                </a>
              </li>
              <li>
                <a href="#gallery" className="text-gray-400 hover:text-white transition-colors">
                  Gallery
                </a>
              </li>
              <li>
                <a href="#engineers" className="text-gray-400 hover:text-white transition-colors">
                  Our Team
                </a>
              </li>
              <li>
                <button
                  onClick={() => setInternshipFormOpen(true)}
                  className="text-gray-400 hover:text-white transition-colors text-left"
                >
                  Internships
                </button>
              </li>
              <li>
                <button
                  onClick={() => setNewsletterFormOpen(true)}
                  className="text-gray-400 hover:text-white transition-colors text-left"
                >
                  Newsletter
                </button>
              </li>
            </ul>
          </div>

          {/* Studio Hours & Social */}
          <div>
            <h4 className="text-white font-semibold mb-4">Hours</h4>
            <p className="text-gray-400 text-sm mb-4">24/7 Booking Available</p>
            <h4 className="text-white font-semibold mb-4">Follow Us</h4>
            <div className="flex space-x-3">
              <a
                href="https://www.instagram.com/highquality.studios/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSocialClick('Instagram')}
                className="w-10 h-10 rounded-full bg-purple-900/30 flex items-center justify-center hover:bg-purple-600 transition-colors"
              >
                <Instagram size={18} className="text-white" />
              </a>
              <a
                href="#"
                onClick={() => trackSocialClick('Facebook')}
                className="w-10 h-10 rounded-full bg-purple-900/30 flex items-center justify-center hover:bg-purple-600 transition-colors"
              >
                <Facebook size={18} className="text-white" />
              </a>
              <a
                href="#"
                onClick={() => trackSocialClick('Twitter')}
                className="w-10 h-10 rounded-full bg-purple-900/30 flex items-center justify-center hover:bg-purple-600 transition-colors"
              >
                <Twitter size={18} className="text-white" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-purple-900/30 pt-6 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} High Quality Studios. All rights reserved.
          </p>
          <div className="flex space-x-6 text-sm">
            <a href="#" className="text-gray-500 hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="text-gray-500 hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>

        {/* Studio Policy */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-gray-500 text-xs">
            <span className="text-green-400">✓</span> Smoking-friendly studio environment
          </p>
          <p className="text-red-400/70 text-xs font-medium">
            All sales are final. No refunds once services are rendered.
          </p>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;